import { getAppStore, setAppStore } from '@/store';
import { LightboxSource } from '@/store/lightbox';
import applyRunResults from '@/utils/generation/applyRunResults';
import { clearGeneration } from '@/utils/storage/generationStorage';

import type { BuiltFlowMedia } from '@/utils/generation/buildFlowMediaList';

/**
 * Final stage of the auto-generation pipeline:
 *  1. rename each file on Drive so the name persists across refreshes
 *  2. replace the loading placeholder with every result the run produced
 *  3. clear the persisted generation entry (recovery handles only mid-poll
 *     crashes — once we have a result, the entry is no longer needed)
 *  4. surface the first result via the lightbox in result mode
 *
 * Steps 1–2 are `applyRunResults`, shared with `useGenerationPolling`: a
 * `presetJsonUrl` run is a run like any other, and keeping the two ends of the
 * app on one implementation is what stops this path from quietly dropping the
 * extra outputs again.
 *
 * The lightbox opens on the first result, over the creations list that
 * `applyRunResults` just wrote, and reaches the siblings through the filmstrip
 * (`AppLightbox` picks them out by the shared `runId`).
 */
const finalizeAutoGeneration = async ({
  generationId,
  placeholderUuid,
  presetTitle,
  built,
  runId,
}: {
  generationId: string;
  placeholderUuid: string;
  presetTitle: string;
  built: BuiltFlowMedia[];
  runId: string;
}): Promise<void> => {
  const timestamp = Date.now();

  const { primaryUuid } = await applyRunResults({
    built,
    placeholderUuid,
    presetTitle,
    runId,
    timestamp,
  });

  await clearGeneration(generationId);

  setAppStore({
    resultFile: built[0].resultFile,
    generationTimestamp: timestamp,
  });

  const items = getAppStore().contentFiles.filter(
    (file) => !file.isLoading && !file.error
  );
  getAppStore().openLightbox({
    items,
    index: items.findIndex((file) => file.uuid === primaryUuid),
    source: LightboxSource.Creations,
    resultMode: true,
  });
};

export default finalizeAutoGeneration;
